import { FC, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { ActionIcon, Button, Tooltip } from "rizzui";
import { toast } from "sonner";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { IoIosAdd } from "react-icons/io";
import { HeadquarterAssetService } from "../../../../../services/headquarters/headquarterAssetService";
import { HeadquarterAssetsApiResponse, HeadquarterAsset } from "../../../../../type/entities/headquarter-asset-type";
import useFetch from "../../../../../hooks/useFetch";
import useFilters from "../../../../../hooks/useFilters";
import CustomTable from "../../../../../components/table/CustomTable";
import { FormDrawerView } from "../../../../../components/forms/FormDrawer";
import DeletePopover from "../../../../../components/buttons/DeletePopover";
import HeadquarterAssetForm from "./HeadquarterAssetForm";
import useHandleErrors from "../../../../../hooks/useHandleErrors";
import moment from "moment";

interface HeadquarterAssetsProps {
}

const columnOrder = [
    'id',
    'type',
    'name',
    'quantity',
    'isOwned',
    'updatedAt',
    'createdAt'
];

const HeadquarterAssets: FC<HeadquarterAssetsProps> = () => {

    // STATES
    const [openFormDrawer, setOpenFormDrawer] = useState(false);
    const [assetSelected, setAssetSelected] = useState<HeadquarterAsset | null>(null);
    const service = new HeadquarterAssetService();

    // HOOKS
    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updatePage, updatePageSize } = useFilters({ headquarter: id });
    const { handleErrors } = useHandleErrors();

    // FETCH DATA
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listAssets({ ...filters, headquarter: id });
        return response.getResponseData() as HeadquarterAssetsApiResponse;
    }, [filters, id]));

    // FUNCTIONS

    const handleDelete = async (assetId: string) => {
        try {
            const response = await (await service.deleteAsset(assetId)).getResponseData();
            if (response.success) {
                toast.success('Activo eliminado correctamente');
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const orderedData = () => data?.data?.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    }) ?? [];

    return (
        <div className="@container">
            <div className="flex justify-end mb-4">
                <Button
                    onClick={() => {
                        setAssetSelected(null);
                        setOpenFormDrawer(true);
                    }}
                    className="w-full md:w-auto"
                >
                    <IoIosAdd className="mr-2 text-lg" />
                    Añadir Activo
                </Button>
            </div>

            <CustomTable
                id="headquarter-assets"
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                filters={filters}
                isSortable
                columnsNoSortables={[]}
                columnsNotShown={['id', 'createdAt']}
                overrideColumns={[
                    {
                        key: 'type',
                        columnNameKey: 'Tipo',
                        render: (row: any) => (
                            <div>
                                {row.row.original.type === 'CONTAINER' ? 'Contenedor' : 
                                 row.row.original.type === 'FILTER' ? 'Filtro' : row.row.original.type}
                            </div>
                        )
                    },
                    {
                        key: 'name',
                        columnNameKey: 'Nombre',
                        render: (row: any) => (
                            <div>
                                {row.row.original.name}
                            </div>
                        )
                    },
                    {
                        key: 'quantity',
                        columnNameKey: 'Cantidad',
                        render: (row: any) => (
                            <div>
                                {row.row.original.quantity}
                            </div>
                        )
                    },
                    {
                        key: 'isOwned',
                        columnNameKey: 'En Propiedad',
                        render: (row: any) => (
                            <div>
                                {row.row.original.isOwned ? 'Sí' : 'No'}
                            </div>
                        )
                    },
                    {
                        key: 'updatedAt',
                        columnNameKey: 'Última Actualización',
                        render: (row: any) => {
                             const date = row.row.original?.updatedAt?.date || row.row.original?.updatedAt;
                             return (
                                <div>
                                    {date ? moment(date).format("DD/MM/YYYY HH:mm") : 'N/A'}
                                </div>
                             )
                        }
                    }
                ]}
                actions={[
                    {
                        label: 'Editar',
                        render: (row: any) => (
                            <Tooltip content="Editar activo">
                                <ActionIcon
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setAssetSelected(row.row.original);
                                        setOpenFormDrawer(true);
                                    }}
                                >
                                    <MdOutlineModeEditOutline />
                                </ActionIcon>
                            </Tooltip>
                        )
                    },
                    {
                        label: 'Eliminar',
                        render: (row: any) => (
                            <DeletePopover
                                title={`Eliminar Activo`}
                                description={`¿Estás seguro de que deseas eliminar este activo? Esta acción no se puede deshacer.`}
                                onDelete={() => handleDelete(row.row.original.id)}
                            />
                        )
                    }
                ]}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: data?.lastPage ?? 1,
                    totalCount: data?.totalRegisters ?? 0,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
            />

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    assetSelected ?
                        "Completa el formulario para editar el activo" :
                        "Completa el formulario para añadir un nuevo activo"
                }
                drawerTitle={assetSelected ? 'Editar Activo' : 'Nuevo Activo'}
                submitText={assetSelected ? 'Guardar Cambios' : 'Crear Activo'}
            >
                <HeadquarterAssetForm
                    setOpenModal={setOpenFormDrawer}
                    refetch={refetch}
                    asset={assetSelected}
                    headquarterId={id}
                />
            </FormDrawerView>
        </div>
    );
};

export default HeadquarterAssets;
